import { Controller } from "@hotwired/stimulus";
import { visit } from "@hotwired/turbo";

/**
 * This controller handles the interaction between the editor and the assistant.
 *
 * It manages:
 * 1. Receiving requests from the editor (via editor:request-assistant event)
 * 2. Checking if there's an active conversation to continue or if a new one should be created
 * 3. Focusing the input of the existing conversation or navigating to create a new one
 *
 * Flow:
 * - Editor dispatches 'editor:request-assistant' when the user asks the Assistant about a selection
 * - Controller checks for existing active conversation
 * - If active conversation exists: focuses its input
 * - If no active conversation: activates assistant tab and navigates to new conversation
 * - On frame load, controller focuses the new conversation's input
 */

export default class extends Controller {
  static targets = ["menu", "menuItem"]
  static values = {
    newConversationPath: { type: String, default: '/asistente/new?context=tools' }
  }

  // Helper method to check if there's an active conversation
  _hasActiveConversation() {
    const conversationFrame = document.getElementById('conversation_frame')
    if (!conversationFrame) return false

    // Check if there are existing messages in the conversation
    const messagesContainer = conversationFrame.querySelector('#messages, [data-messages-list-target="messages"]')
    if (messagesContainer) {
      const messages = messagesContainer.querySelectorAll('[data-role="message"], .assistant--message, .message')
      if (messages.length > 0) return true
    }

    // Check if there's a conversation header indicating an active conversation
    const conversationHeader = conversationFrame.querySelector('#conversation-context, [id^="conversation-"], .conversation-header')
    if (conversationHeader) return true

    // Check if there's a message input that suggests an active conversation
    const messageForm = conversationFrame.querySelector('form[action*="/messages"], form[data-turbo-frame="conversation_frame"]')
    if (messageForm) {
      // Look for hidden inputs that indicate an existing conversation
      const conversationInput = messageForm.querySelector('input[name*="conversation"]')
      if (conversationInput && conversationInput.value) return true
    }

    return false
  }

  _focusConversationInput() {
    const inputField = this._conversationInput()
    if (!inputField) return false

    inputField.focus()
    return true
  }

  _conversationInput() {
    const conversationFrame = document.getElementById('conversation_frame')
    return conversationFrame?.querySelector('textarea[name*="question"], input[name*="question"], textarea[data-conversation-menu-target="input"]')
  }

  connect() {
    this._bindEventHandlers();
  }

  disconnect() {
    this._removeEventHandlers();
  }

  handleEditorRequest(event) {
    const { tab, eventId } = event.detail;

    // Check if there's already an active conversation
    if (this._hasActiveConversation()) {
      if (this._focusConversationInput()) {
        // Make sure the assistant tab is active
        if (tab) {
          this.activateTab(tab);
        }
        return;
      }
    }

    // No active conversation or couldn't add to existing, create new conversation
    this._storePendingState(tab, eventId);

    if (this.pendingTab) {
      this.activateTab(this.pendingTab);
    }

    this._handleNavigation();
  }

  frameLoaded() {
    if (!this.pendingFocus) return;
    this._conversationInput()?.focus();
    this._clearPendingState();
  }

  _storePendingState(tab, eventId) {
    this.pendingFocus = true;
    this.pendingTab = tab;
    this.pendingEventId = eventId;
  }

  _clearPendingState() {
    this.pendingFocus = false;
    this.pendingEventId = null;
  }

  _handleNavigation() {
    const currentPath = window.location.pathname;

    if (currentPath === '/asistente/new') {
      this.frameLoaded();
    } else {
      this._navigateToNewConversation();
    }
  }

  _navigateToNewConversation() {
    const url = new URL(this.newConversationPathValue, window.location.origin);

    // If we are in the Writer, ensure that writer_document_id is in the URL
    const conversationFrame = document.getElementById('conversation_frame')
    if (conversationFrame) {
      const writerDocumentId = conversationFrame.dataset.writerDocumentId
      if (writerDocumentId) {
        // Add writer_document_id to the prompt_params if it is not present
        if (!url.searchParams.has('prompt_params[writer_document_id]')) {
          url.searchParams.set('prompt_params[writer_document_id]', writerDocumentId)
        }
      }
    }

    visit(url.toString(), { frame: 'conversation_frame' });
  }

  _bindEventHandlers() {
    this.handleEditorRequest = this.handleEditorRequest.bind(this);
    this.frameLoaded = this.frameLoaded.bind(this);
    window.addEventListener('editor:request-assistant', this.handleEditorRequest);
    window.addEventListener('turbo:frame-load', this.frameLoaded);
  }

  _removeEventHandlers() {
    window.removeEventListener('editor:request-assistant', this.handleEditorRequest);
    window.removeEventListener('turbo:frame-load', this.frameLoaded);
  }

  activateTab(tabSpec, attempts = 0, maxAttempts = 10) {
    if (!tabSpec || attempts >= maxAttempts) return;

    const [tabsId, tabId] = tabSpec.split('#');
    if (!tabsId || !tabId) return;

    const tabsElement = document.getElementById(tabsId);
    if (!tabsElement) {
      setTimeout(() => this.activateTab(tabSpec, attempts + 1, maxAttempts), 100);
      return;
    }

    const tabsController = this.application.getControllerForElementAndIdentifier(tabsElement, 'tabs');
    if (!tabsController) {
      setTimeout(() => this.activateTab(tabSpec, attempts + 1, maxAttempts), 100);
      return;
    }

    const tabItem = tabsController.tabItemTargets.find(item =>
      item.dataset.tabsControlsParam === tabId
    );

    if (tabItem) {
      setTimeout(() => tabItem.click(), 50);
    } else {
      setTimeout(() => this.activateTab(tabSpec, attempts + 1, maxAttempts), 100);
    }
  }
}
